import React, { useEffect, useState, useMemo } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';

import './styles.scss';

/**
 * Dropdown de filtro em formato árvore: nível superior + itens filhos.
 * Genérico: serve para regional, canal ou qualquer filtro hierárquico.
 *
 * Estrutura esperada de dadosArvore: array de
 *   { id, nome, [nomePropFilhos]: [{ id, nome }, ...] }
 * Ex.: nomePropFilhos = "regionais" => { id, nome, regionais: [...] }
 *
 * Seleção: (tudo), todos os filhos de um superior, ou item individual.
 * Mantém seleção como array de strings (nomes) para o contrato da API.
 *
 * @param {string} nome - Chave do filtro em selecao/setSelecao (ex.: "regional", "canal")
 * @param {Array} dadosArvore - Array de { id, nome, [nomePropFilhos]: [...] }
 * @param {Array} filtroDisp - Valores disponíveis (nomes). Se vazio/null, todos da árvore são considerados disponíveis
 * @param {string} nomePropFilhos - Nome da propriedade dos filhos em cada nó (default: "regionais")
 */
const DropDownFiltroArvore = (props) => {
  const {
    setFetchDataTrigger,
    nome,
    dadosArvore,
    filtroDisp,
    selecao,
    setSelecao,
    nomePropFilhos = 'regionais',
  } = props;

  const [show, setShow] = useState(false);
  const [tmpSelecao, setTmpSelecao] = useState(selecao?.[nome] ?? []);

  const filhosDe = (no) => no[nomePropFilhos] ?? [];

  // Lista plana de todos os nomes dos filhos da árvore
  const todosNomesFilhos = useMemo(() => {
    if (!Array.isArray(dadosArvore)) return [];
    return dadosArvore.flatMap((sup) => filhosDe(sup).map((r) => r.nome));
  }, [dadosArvore, nomePropFilhos]);

  // Referência de "disponíveis": filtroDisp se tiver itens, senão todos da árvore
  const listaRef = useMemo(() => {
    if (Array.isArray(filtroDisp) && filtroDisp.length > 0) return filtroDisp;
    return todosNomesFilhos;
  }, [filtroDisp, todosNomesFilhos]);

  const qtdRef = listaRef.length;

  const tudo = qtdRef > 0 && (tmpSelecao?.length ?? 0) === qtdRef;

  useEffect(() => {
    setTmpSelecao(selecao?.[nome] ?? []);
  }, [selecao, nome]);

  const toggleShow = () => setShow((prev) => !prev);

  const aplicarSelecao = () => {
    const tmp = { ...selecao };
    tmp[nome] = Array.isArray(tmpSelecao) ? [...tmpSelecao] : [];
    setSelecao(tmp);
    setFetchDataTrigger((prev) => !prev);
    toggleShow();
  };

  const cancelarSelecao = () => {
    setTmpSelecao(selecao?.[nome] ?? []);
    toggleShow();
  };

  const mudarTodos = (value) => {
    if (value) {
      setTmpSelecao([...listaRef]);
    } else {
      setTmpSelecao([]);
    }
  };

  const isDisponivel = (nomeItem) => {
    if (!Array.isArray(filtroDisp) || filtroDisp.length === 0) return true;
    return filtroDisp.includes(nomeItem);
  };

  const toggleFilho = (nomeItem, checked) => {
    setTmpSelecao((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (checked) {
        if (!arr.includes(nomeItem)) arr.push(nomeItem);
      } else {
        const i = arr.indexOf(nomeItem);
        if (i !== -1) arr.splice(i, 1);
      }
      return arr;
    });
  };

  const toggleSuperior = (superior, checked) => {
    const nomesFilhos = filhosDe(superior).map((r) => r.nome);
    const disponiveisDesteSuperior = nomesFilhos.filter((n) => listaRef.includes(n));
    setTmpSelecao((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (checked) {
        disponiveisDesteSuperior.forEach((n) => {
          if (!arr.includes(n)) arr.push(n);
        });
      } else {
        disponiveisDesteSuperior.forEach((n) => {
          const i = arr.indexOf(n);
          if (i !== -1) arr.splice(i, 1);
        });
      }
      return arr;
    });
  };

  const todosFilhosSelecionados = (superior) => {
    const nomes = filhosDe(superior)
      .map((r) => r.nome)
      .filter((n) => listaRef.includes(n));
    if (nomes.length === 0) return false;
    return nomes.every((n) => tmpSelecao?.includes(n));
  };

  if (!dadosArvore?.length) return null;

  return (
    <Dropdown drop="down" show={show} onToggle={(isOpen) => setShow(isOpen)}>
      <Dropdown.Toggle split>
        {qtdRef > 0 && selecao?.[nome]?.length === qtdRef ? '(Tudo)' : '(Valores múltiplos)'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Form.Check
          key="tudo"
          type="checkbox"
          label="(tudo)"
          id={`tudo-${nome}`}
          checked={tudo}
          onChange={(e) => mudarTodos(e.target.checked)}
        />
        <Dropdown.Divider />
        <div className="dropdonwn-scrollable">
          {dadosArvore.map((superior) => {
            const filhos = filhosDe(superior);
            const superiorTemDisponiveis = filhos.some((f) => listaRef.includes(f.nome));
            return (
              <div key={superior.id ?? superior.nome} className="filtro-arvore-item">
                <Form.Check
                  type="checkbox"
                  id={`sup-${nome}-${superior.id}`}
                  className="filtro-arvore-superior"
                  label={superior.nome ?? 'Nulo'}
                  checked={todosFilhosSelecionados(superior)}
                  disabled={!superiorTemDisponiveis}
                  onChange={(e) => toggleSuperior(superior, e.target.checked)}
                />
                <div className="filtro-arvore-subitens">
                  {filhos.map((filho) => (
                    <Form.Check
                      key={filho.id ?? filho.nome}
                      type="checkbox"
                      id={`filho-${nome}-${filho.id}-${filho.nome}`}
                      className="filtro-arvore-subitem"
                      label={filho.nome ?? 'Nulo'}
                      checked={tmpSelecao?.includes(filho.nome)}
                      disabled={!isDisponivel(filho.nome)}
                      onChange={(e) => toggleFilho(filho.nome, e.target.checked)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <Dropdown.Divider />
        <div className="btn-container">
          <button type="button" onClick={cancelarSelecao}>
            Cancelar
          </button>
          <button className="btn-aplicar" type="button" onClick={aplicarSelecao}>
            Aplicar
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DropDownFiltroArvore;
